// app/src/lib/tokenStore.js — Cognito JWT storage.
//
// **Tokens are stored ONLY in IndexedDB. Never in localStorage.** The
// scripts/lint-no-localstorage.mjs check fails any commit that violates this.
//
// We store the refresh token alongside an integrity tag derived from the per-device
// salt so that if an attacker substitutes a refresh token (e.g. via DOM clobbering
// or extension tampering), `getTokens` rejects it.

import * as kv from './idbKv.js';
import { sha256Hex } from './crypto.js';

const KEY = 'crew.cognito.tokens.v1';

/**
 * @typedef {Object} Tokens
 * @property {string} idToken         - JWT, contains `sub`.
 * @property {string} accessToken     - JWT, 15-min lifetime.
 * @property {string} refreshToken    - opaque, 30-day lifetime.
 * @property {number} accessExpiresAt - epoch ms.
 * @property {number} issuedAt        - epoch ms.
 */

/**
 * @typedef {Object} StoredTokens
 * @property {Tokens} tokens
 * @property {string} integrity       - SHA-256(refreshToken || saltHex).
 */

/**
 * Persist tokens. Always replaces — never merge — so a partial write cannot leave
 * stale fields behind.
 * @param {Tokens} tokens
 * @param {Uint8Array} salt - 32-byte per-device salt (from idbKv).
 */
export async function setTokens(tokens, salt) {
  validateShape(tokens);
  const integrity = await sha256Hex(tokens.refreshToken + bytesToHex(salt));
  /** @type {StoredTokens} */
  const stored = { tokens, integrity };
  await kv.set(KEY, stored);
}

/**
 * Read and integrity-check tokens. Returns null if missing or tampered.
 * @param {Uint8Array} salt
 * @returns {Promise<Tokens | null>}
 */
export async function getTokens(salt) {
  /** @type {StoredTokens | undefined} */
  const stored = await kv.get(KEY);
  if (!stored || !stored.tokens || !stored.integrity) return null;
  const expected = await sha256Hex(stored.tokens.refreshToken + bytesToHex(salt));
  if (expected !== stored.integrity) {
    // Tampered: refuse and wipe.
    await clearTokens();
    return null;
  }
  return stored.tokens;
}

export async function clearTokens() {
  await kv.del(KEY);
}

function validateShape(t) {
  if (!t || typeof t !== 'object') throw new Error('tokens: not an object');
  for (const k of ['idToken', 'accessToken', 'refreshToken']) {
    if (typeof t[k] !== 'string' || !t[k]) throw new Error(`tokens.${k}: missing`);
  }
  if (typeof t.accessExpiresAt !== 'number' || typeof t.issuedAt !== 'number') {
    throw new Error('tokens: missing timestamps');
  }
}

function bytesToHex(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}
